import { ResourceType } from '../proto/api.js';
import { ArmorType, Class, ItemSlot, Profession, PseudoStat, Race, RangedWeaponType, Stat, WeaponType } from '../proto/common.js';
import { DungeonDifficulty, RaidFilterOption, RepFaction, RepLevel, SourceFilterOption } from '../proto/ui.js';
import { t } from '../i18n/index.js';

// ===== I18N HELPER FUNCTIONS =====
// These functions provide translated names dynamically based on current locale

export function getArmorTypeName(armorType: ArmorType): string {
	const key = `armorTypes.${{
		[ArmorType.ArmorTypeUnknown]: 'unknown',
		[ArmorType.ArmorTypeCloth]: 'cloth',
		[ArmorType.ArmorTypeLeather]: 'leather',
		[ArmorType.ArmorTypeMail]: 'mail',
		[ArmorType.ArmorTypePlate]: 'plate',
	}[armorType] || 'unknown'}`;
	return t(key);
}

export function getWeaponTypeName(weaponType: WeaponType): string {
	const key = `weaponTypes.${{
		[WeaponType.WeaponTypeUnknown]: 'unknown',
		[WeaponType.WeaponTypeAxe]: 'axe',
		[WeaponType.WeaponTypeDagger]: 'dagger',
		[WeaponType.WeaponTypeFist]: 'fist',
		[WeaponType.WeaponTypeMace]: 'mace',
		[WeaponType.WeaponTypeOffHand]: 'offHand',
		[WeaponType.WeaponTypePolearm]: 'polearm',
		[WeaponType.WeaponTypeShield]: 'shield',
		[WeaponType.WeaponTypeStaff]: 'staff',
		[WeaponType.WeaponTypeSword]: 'sword',
	}[weaponType] || 'unknown'}`;
	return t(key);
}

export function getRangedWeaponTypeName(rangedWeaponType: RangedWeaponType): string {
	const key = `rangedWeaponTypes.${{
		[RangedWeaponType.RangedWeaponTypeUnknown]: 'unknown',
		[RangedWeaponType.RangedWeaponTypeBow]: 'bow',
		[RangedWeaponType.RangedWeaponTypeCrossbow]: 'crossbow',
		[RangedWeaponType.RangedWeaponTypeGun]: 'gun',
		[RangedWeaponType.RangedWeaponTypeIdol]: 'idol',
		[RangedWeaponType.RangedWeaponTypeLibram]: 'libram',
		[RangedWeaponType.RangedWeaponTypeSigil]: 'sigil',
		[RangedWeaponType.RangedWeaponTypeThrown]: 'thrown',
		[RangedWeaponType.RangedWeaponTypeTotem]: 'totem',
		[RangedWeaponType.RangedWeaponTypeWand]: 'wand',
	}[rangedWeaponType] || 'unknown'}`;
	return t(key);
}

export function getRaceName(race: Race): string {
	const key = `races.${{
		[Race.RaceUnknown]: 'unknown',
		[Race.RaceBloodElf]: 'bloodElf',
		[Race.RaceDraenei]: 'draenei',
		[Race.RaceDwarf]: 'dwarf',
		[Race.RaceGnome]: 'gnome',
		[Race.RaceHuman]: 'human',
		[Race.RaceNightElf]: 'nightElf',
		[Race.RaceOrc]: 'orc',
		[Race.RaceTauren]: 'tauren',
		[Race.RaceTroll]: 'troll',
		[Race.RaceUndead]: 'undead',
	}[race] || 'unknown'}`;
	return t(key);
}

export function getClassName(playerClass: Class): string {
	const key = `classes.${{
		[Class.ClassUnknown]: 'unknown',
		[Class.ClassDruid]: 'druid',
		[Class.ClassHunter]: 'hunter',
		[Class.ClassMage]: 'mage',
		[Class.ClassPaladin]: 'paladin',
		[Class.ClassPriest]: 'priest',
		[Class.ClassRogue]: 'rogue',
		[Class.ClassShaman]: 'shaman',
		[Class.ClassWarlock]: 'warlock',
		[Class.ClassWarrior]: 'warrior',
		[Class.ClassDeathknight]: 'deathknight',
	}[playerClass] || 'unknown'}`;
	return t(key);
}

export function getProfessionName(profession: Profession): string {
	const key = `professions.${{
		[Profession.ProfessionUnknown]: 'unknown',
		[Profession.Alchemy]: 'alchemy',
		[Profession.Blacksmithing]: 'blacksmithing',
		[Profession.Enchanting]: 'enchanting',
		[Profession.Engineering]: 'engineering',
		[Profession.Herbalism]: 'herbalism',
		[Profession.Inscription]: 'inscription',
		[Profession.Jewelcrafting]: 'jewelcrafting',
		[Profession.Leatherworking]: 'leatherworking',
		[Profession.Mining]: 'mining',
		[Profession.Skinning]: 'skinning',
		[Profession.Tailoring]: 'tailoring',
	}[profession] || 'unknown'}`;
	return t(key);
}

export function getStatName(stat: Stat): string {
	const key = `stats.${{
		[Stat.StatStrength]: 'strength',
		[Stat.StatAgility]: 'agility',
		[Stat.StatStamina]: 'stamina',
		[Stat.StatIntellect]: 'intellect',
		[Stat.StatSpirit]: 'spirit',
		[Stat.StatSpellPower]: 'spellPower',
		[Stat.StatMP5]: 'mp5',
		[Stat.StatSpellHit]: 'spellHit',
		[Stat.StatSpellCrit]: 'spellCrit',
		[Stat.StatSpellHaste]: 'spellHaste',
		[Stat.StatSpellPenetration]: 'spellPenetration',
		[Stat.StatAttackPower]: 'attackPower',
		[Stat.StatMeleeHit]: 'meleeHit',
		[Stat.StatMeleeCrit]: 'meleeCrit',
		[Stat.StatMeleeHaste]: 'meleeHaste',
		[Stat.StatArmorPenetration]: 'armorPenetration',
		[Stat.StatExpertise]: 'expertise',
		[Stat.StatMana]: 'mana',
		[Stat.StatEnergy]: 'energy',
		[Stat.StatRage]: 'rage',
		[Stat.StatArmor]: 'armor',
		[Stat.StatRangedAttackPower]: 'rangedAttackPower',
		[Stat.StatDefense]: 'defense',
		[Stat.StatBlock]: 'block',
		[Stat.StatBlockValue]: 'blockValue',
		[Stat.StatDodge]: 'dodge',
		[Stat.StatParry]: 'parry',
		[Stat.StatResilience]: 'resilience',
		[Stat.StatHealth]: 'health',
		[Stat.StatArcaneResistance]: 'arcaneResistance',
		[Stat.StatFireResistance]: 'fireResistance',
		[Stat.StatFrostResistance]: 'frostResistance',
		[Stat.StatNatureResistance]: 'natureResistance',
		[Stat.StatShadowResistance]: 'shadowResistance',
		[Stat.StatBonusArmor]: 'bonusArmor',
		[Stat.StatRunicPower]: 'runicPower',
		[Stat.StatBloodRune]: 'bloodRune',
		[Stat.StatFrostRune]: 'frostRune',
		[Stat.StatUnholyRune]: 'unholyRune',
		[Stat.StatDeathRune]: 'deathRune',
	}[stat] || 'strength'}`; // fallback to strength
	return t(key);
}

export function getPseudoStatName(stat: PseudoStat): string {
	switch (stat) {
		case PseudoStat.PseudoStatMainHandDps: return t('gear.slot.mainHand') + ' DPS';
		case PseudoStat.PseudoStatOffHandDps: return t('gear.slot.offHand') + ' DPS';
		case PseudoStat.PseudoStatRangedDps: return t('gear.slot.ranged') + ' DPS';
		case PseudoStat.PseudoStatBlockValueMultiplier: return t('stats.blockValue') + ' Multiplier';
		case PseudoStat.PseudoStatDodge: return t('stats.dodge') + ' Chance';
		case PseudoStat.PseudoStatParry: return t('stats.parry') + ' Chance';
		default: return 'Unknown';
	}
}

export function getSlotName(slot: ItemSlot): string {
	const key = `gear.slot.${{
		[ItemSlot.ItemSlotHead]: 'head',
		[ItemSlot.ItemSlotNeck]: 'neck',
		[ItemSlot.ItemSlotShoulder]: 'shoulder',
		[ItemSlot.ItemSlotBack]: 'back',
		[ItemSlot.ItemSlotChest]: 'chest',
		[ItemSlot.ItemSlotWrist]: 'wrist',
		[ItemSlot.ItemSlotHands]: 'hands',
		[ItemSlot.ItemSlotWaist]: 'waist',
		[ItemSlot.ItemSlotLegs]: 'legs',
		[ItemSlot.ItemSlotFeet]: 'feet',
		[ItemSlot.ItemSlotFinger1]: 'finger1',
		[ItemSlot.ItemSlotFinger2]: 'finger2',
		[ItemSlot.ItemSlotTrinket1]: 'trinket1',
		[ItemSlot.ItemSlotTrinket2]: 'trinket2',
		[ItemSlot.ItemSlotMainHand]: 'mainHand',
		[ItemSlot.ItemSlotOffHand]: 'offHand',
		[ItemSlot.ItemSlotRanged]: 'ranged',
	}[slot] || 'head'}`;
	return t(key);
}

export function getResourceName(resource: ResourceType): string {
	const key = `resources.${{
		[ResourceType.ResourceTypeNone]: 'none',
		[ResourceType.ResourceTypeHealth]: 'health',
		[ResourceType.ResourceTypeMana]: 'mana',
		[ResourceType.ResourceTypeEnergy]: 'energy',
		[ResourceType.ResourceTypeRage]: 'rage',
		[ResourceType.ResourceTypeComboPoints]: 'comboPoints',
		[ResourceType.ResourceTypeFocus]: 'focus',
		[ResourceType.ResourceTypeRunicPower]: 'runicPower',
		[ResourceType.ResourceTypeBloodRune]: 'bloodRune',
		[ResourceType.ResourceTypeFrostRune]: 'frostRune',
		[ResourceType.ResourceTypeUnholyRune]: 'unholyRune',
		[ResourceType.ResourceTypeDeathRune]: 'deathRune',
	}[resource] || 'none'}`;
	return t(key);
}

export function getSourceName(source: SourceFilterOption): string {
	const key = `filters.source.${{
		[SourceFilterOption.SourceUnknown]: 'unknown',
		[SourceFilterOption.SourceCrafting]: 'crafting',
		[SourceFilterOption.SourceQuest]: 'quest',
		[SourceFilterOption.SourceDungeon]: 'dungeon',
		[SourceFilterOption.SourceDungeonH]: 'dungeonH',
		[SourceFilterOption.SourceDungeonTRA]: 'dungeonTRA',
		[SourceFilterOption.SourceDungeonTRB]: 'dungeonTRB',
		[SourceFilterOption.SourceRaid10]: 'raid10',
		[SourceFilterOption.SourceRaid10H]: 'raid10H',
		[SourceFilterOption.SourceRaid25]: 'raid25',
		[SourceFilterOption.SourceRaid25H]: 'raid25H',
	}[source] || 'unknown'}`;
	return t(key);
}

export function getRaidName(raid: RaidFilterOption): string {
	const key = `filters.raid.${{
		[RaidFilterOption.RaidUnknown]: 'unknown',
		[RaidFilterOption.RaidVanilla]: 'vanilla',
		[RaidFilterOption.RaidTbc]: 'tbc',
		[RaidFilterOption.RaidNaxxramas]: 'naxxramas',
		[RaidFilterOption.RaidEyeOfEternity]: 'eyeOfEternity',
		[RaidFilterOption.RaidObsidianSanctum]: 'obsidianSanctum',
		[RaidFilterOption.RaidVaultOfArchavon]: 'vaultOfArchavon',
		[RaidFilterOption.RaidUlduar]: 'ulduar',
		[RaidFilterOption.RaidTrialOfTheCrusader]: 'trialOfTheCrusader',
		[RaidFilterOption.RaidOnyxiasLair]: 'onyxiasLair',
		[RaidFilterOption.RaidIcecrownCitadel]: 'icecrownCitadel',
		[RaidFilterOption.RaidRubySanctum]: 'rubySanctum',
	}[raid] || 'unknown'}`;
	return t(key);
}

export function getDifficultyName(difficulty: DungeonDifficulty): string {
	const key = `difficulty.${{
		[DungeonDifficulty.DifficultyUnknown]: 'unknown',
		[DungeonDifficulty.DifficultyNormal]: 'normal',
		[DungeonDifficulty.DifficultyHeroic]: 'heroic',
		[DungeonDifficulty.DifficultyTitanRuneAlpha]: 'titanRuneAlpha',
		[DungeonDifficulty.DifficultyTitanRuneBeta]: 'titanRuneBeta',
		[DungeonDifficulty.DifficultyRaid10]: 'raid10',
		[DungeonDifficulty.DifficultyRaid10H]: 'raid10H',
		[DungeonDifficulty.DifficultyRaid25]: 'raid25',
		[DungeonDifficulty.DifficultyRaid25H]: 'raid25H',
	}[difficulty] || 'unknown'}`;
	return t(key);
}

export function getRepLevelName(repLevel: RepLevel): string {
	const key = `repLevel.${{
		[RepLevel.RepLevelUnknown]: 'unknown',
		[RepLevel.RepLevelHated]: 'hated',
		[RepLevel.RepLevelHostile]: 'hostile',
		[RepLevel.RepLevelUnfriendly]: 'unfriendly',
		[RepLevel.RepLevelNeutral]: 'neutral',
		[RepLevel.RepLevelFriendly]: 'friendly',
		[RepLevel.RepLevelHonored]: 'honored',
		[RepLevel.RepLevelRevered]: 'revered',
		[RepLevel.RepLevelExalted]: 'exalted',
	}[repLevel] || 'unknown'}`;
	return t(key);
}

// ===== BACKWARD COMPATIBILITY MAPS =====
// These Maps are maintained for backward compatibility but now use the i18n helper functions
// They will be updated when UI locale changes through the uiLocaleChangeEmitter

// Internal function to create a reactive Map that updates on locale change
function createReactiveMap<T>(getterFn: (key: T) => string): Map<T, string> {
	const map = new Map<T, string>();
	// The Map will be populated on-demand through getter functions
	return map;
}

// We keep the Maps for backward compatibility but they're now dynamically generated
// Code using these Maps will get English values initially, but we recommend migrating
// to the getter functions for full i18n support

export const armorTypeNames: Map<ArmorType, string> = new Map([
	[ArmorType.ArmorTypeUnknown, 'Unknown'],
	[ArmorType.ArmorTypeCloth, 'Cloth'],
	[ArmorType.ArmorTypeLeather, 'Leather'],
	[ArmorType.ArmorTypeMail, 'Mail'],
	[ArmorType.ArmorTypePlate, 'Plate'],
]);

export const weaponTypeNames: Map<WeaponType, string> = new Map([
	[WeaponType.WeaponTypeUnknown, 'Unknown'],
	[WeaponType.WeaponTypeAxe, 'Axe'],
	[WeaponType.WeaponTypeDagger, 'Dagger'],
	[WeaponType.WeaponTypeFist, 'Fist'],
	[WeaponType.WeaponTypeMace, 'Mace'],
	[WeaponType.WeaponTypeOffHand, 'Misc'],
	[WeaponType.WeaponTypePolearm, 'Polearm'],
	[WeaponType.WeaponTypeShield, 'Shield'],
	[WeaponType.WeaponTypeStaff, 'Staff'],
	[WeaponType.WeaponTypeSword, 'Sword'],
]);

export const rangedWeaponTypeNames: Map<RangedWeaponType, string> = new Map([
	[RangedWeaponType.RangedWeaponTypeUnknown, 'Unknown'],
	[RangedWeaponType.RangedWeaponTypeBow, 'Bow'],
	[RangedWeaponType.RangedWeaponTypeCrossbow, 'Crossbow'],
	[RangedWeaponType.RangedWeaponTypeGun, 'Gun'],
	[RangedWeaponType.RangedWeaponTypeIdol, 'Idol'],
	[RangedWeaponType.RangedWeaponTypeLibram, 'Libram'],
	[RangedWeaponType.RangedWeaponTypeSigil, 'Sigil'],
	[RangedWeaponType.RangedWeaponTypeThrown, 'Thrown'],
	[RangedWeaponType.RangedWeaponTypeTotem, 'Totem'],
	[RangedWeaponType.RangedWeaponTypeWand, 'Wand'],
]);

export const raceNames: Map<Race, string> = new Map([
	[Race.RaceUnknown, 'None'],
	[Race.RaceBloodElf, 'Blood Elf'],
	[Race.RaceDraenei, 'Draenei'],
	[Race.RaceDwarf, 'Dwarf'],
	[Race.RaceGnome, 'Gnome'],
	[Race.RaceHuman, 'Human'],
	[Race.RaceNightElf, 'Night Elf'],
	[Race.RaceOrc, 'Orc'],
	[Race.RaceTauren, 'Tauren'],
	[Race.RaceTroll, 'Troll'],
	[Race.RaceUndead, 'Undead'],
]);

export function nameToRace(name: string): Race {
	const normalized = name.toLowerCase().replaceAll(' ', '');
	for (const [key, value] of raceNames) {
		if (value.toLowerCase().replaceAll(' ', '') == normalized) {
			return key;
		}
	}
	return Race.RaceUnknown;
}

export const classNames: Map<Class, string> = new Map([
	[Class.ClassUnknown, 'None'],
	[Class.ClassDruid, 'Druid'],
	[Class.ClassHunter, 'Hunter'],
	[Class.ClassMage, 'Mage'],
	[Class.ClassPaladin, 'Paladin'],
	[Class.ClassPriest, 'Priest'],
	[Class.ClassRogue, 'Rogue'],
	[Class.ClassShaman, 'Shaman'],
	[Class.ClassWarlock, 'Warlock'],
	[Class.ClassWarrior, 'Warrior'],
	[Class.ClassDeathknight, 'Death Knight'],
]);

export function nameToClass(name: string): Class {
	const lower = name.toLowerCase();
	for (const [key, value] of classNames) {
		if (value.toLowerCase().replace(/\s+/g, '') == lower) {
			return key;
		}
	}
	return Class.ClassUnknown;
}

export const professionNames: Map<Profession, string> = new Map([
	[Profession.ProfessionUnknown, 'None'],
	[Profession.Alchemy, 'Alchemy'],
	[Profession.Blacksmithing, 'Blacksmithing'],
	[Profession.Enchanting, 'Enchanting'],
	[Profession.Engineering, 'Engineering'],
	[Profession.Herbalism, 'Herbalism'],
	[Profession.Inscription, 'Inscription'],
	[Profession.Jewelcrafting, 'Jewelcrafting'],
	[Profession.Leatherworking, 'Leatherworking'],
	[Profession.Mining, 'Mining'],
	[Profession.Skinning, 'Skinning'],
	[Profession.Tailoring, 'Tailoring'],
]);

export function nameToProfession(name: string): Profession {
	const lower = name.toLowerCase();
	for (const [key, value] of professionNames) {
		if (value.toLowerCase() == lower) {
			return key;
		}
	}
	return Profession.ProfessionUnknown;
}

export const statOrder: Array<Stat> = [
	Stat.StatHealth,
	Stat.StatMana,
	Stat.StatArmor,
	Stat.StatBonusArmor,
	Stat.StatStamina,
	Stat.StatStrength,
	Stat.StatAgility,
	Stat.StatIntellect,
	Stat.StatSpirit,
	Stat.StatSpellPower,
	Stat.StatSpellHit,
	Stat.StatSpellCrit,
	Stat.StatSpellHaste,
	Stat.StatSpellPenetration,
	Stat.StatMP5,
	Stat.StatAttackPower,
	Stat.StatRangedAttackPower,
	Stat.StatMeleeHit,
	Stat.StatMeleeCrit,
	Stat.StatMeleeHaste,
	Stat.StatArmorPenetration,
	Stat.StatExpertise,
	Stat.StatEnergy,
	Stat.StatRage,
	Stat.StatDefense,
	Stat.StatBlock,
	Stat.StatBlockValue,
	Stat.StatDodge,
	Stat.StatParry,
	Stat.StatResilience,
	Stat.StatArcaneResistance,
	Stat.StatFireResistance,
	Stat.StatFrostResistance,
	Stat.StatNatureResistance,
	Stat.StatShadowResistance,
	Stat.StatRunicPower,
	Stat.StatBloodRune,
	Stat.StatFrostRune,
	Stat.StatUnholyRune,
	Stat.StatDeathRune,
];

export const statNames: Map<Stat, string> = new Map([
	[Stat.StatStrength, 'Strength'],
	[Stat.StatAgility, 'Agility'],
	[Stat.StatStamina, 'Stamina'],
	[Stat.StatIntellect, 'Intellect'],
	[Stat.StatSpirit, 'Spirit'],
	[Stat.StatSpellPower, 'Spell Dmg'],
	[Stat.StatMP5, 'MP5'],
	[Stat.StatSpellHit, 'Spell Hit'],
	[Stat.StatSpellCrit, 'Spell Crit'],
	[Stat.StatSpellHaste, 'Spell Haste'],
	[Stat.StatSpellPenetration, 'Spell Pen'],
	[Stat.StatAttackPower, 'Attack Power'],
	[Stat.StatMeleeHit, 'Melee Hit'],
	[Stat.StatMeleeCrit, 'Melee Crit'],
	[Stat.StatMeleeHaste, 'Melee Haste'],
	[Stat.StatArmorPenetration, 'Armor Pen'],
	[Stat.StatExpertise, 'Expertise'],
	[Stat.StatMana, 'Mana'],
	[Stat.StatEnergy, 'Energy'],
	[Stat.StatRage, 'Rage'],
	[Stat.StatArmor, 'Armor'],
	[Stat.StatRangedAttackPower, 'Ranged AP'],
	[Stat.StatDefense, 'Defense'],
	[Stat.StatBlock, 'Block'],
	[Stat.StatBlockValue, 'Block Value'],
	[Stat.StatDodge, 'Dodge'],
	[Stat.StatParry, 'Parry'],
	[Stat.StatResilience, 'Resilience'],
	[Stat.StatHealth, 'Health'],
	[Stat.StatArcaneResistance, 'Arcane Resistance'],
	[Stat.StatFireResistance, 'Fire Resistance'],
	[Stat.StatFrostResistance, 'Frost Resistance'],
	[Stat.StatNatureResistance, 'Nature Resistance'],
	[Stat.StatShadowResistance, 'Shadow Resistance'],
	[Stat.StatBonusArmor, 'Bonus Armor'],
	[Stat.StatRunicPower, 'Runic Power'],
	[Stat.StatBloodRune, 'Blood Rune'],
	[Stat.StatFrostRune, 'Frost Rune'],
	[Stat.StatUnholyRune, 'Unholy Rune'],
	[Stat.StatDeathRune, 'Death Rune'],
]);

export const pseudoStatOrder: Array<PseudoStat> = [
	PseudoStat.PseudoStatMainHandDps,
	PseudoStat.PseudoStatOffHandDps,
	PseudoStat.PseudoStatRangedDps,
	PseudoStat.PseudoStatBlockValueMultiplier,
];
export const pseudoStatNames: Map<PseudoStat, string> = new Map([
	[PseudoStat.PseudoStatMainHandDps, 'Main Hand DPS'],
	[PseudoStat.PseudoStatOffHandDps, 'Off Hand DPS'],
	[PseudoStat.PseudoStatRangedDps, 'Ranged DPS'],
	[PseudoStat.PseudoStatBlockValueMultiplier, 'Block Value Multiplier'],
	[PseudoStat.PseudoStatDodge, 'Dodge Chance'],
	[PseudoStat.PseudoStatParry, 'Parry Chance'],
]);

export function getClassStatName(stat: Stat, playerClass: Class): string {
	const baseName = getStatName(stat);
	if (playerClass == Class.ClassHunter) {
		return baseName.replace('Melee', 'Ranged');
	} else {
		return baseName;
	}
}

export const slotNames: Map<ItemSlot, string> = new Map([
	[ItemSlot.ItemSlotHead, 'Head'],
	[ItemSlot.ItemSlotNeck, 'Neck'],
	[ItemSlot.ItemSlotShoulder, 'Shoulders'],
	[ItemSlot.ItemSlotBack, 'Back'],
	[ItemSlot.ItemSlotChest, 'Chest'],
	[ItemSlot.ItemSlotWrist, 'Wrist'],
	[ItemSlot.ItemSlotHands, 'Hands'],
	[ItemSlot.ItemSlotWaist, 'Waist'],
	[ItemSlot.ItemSlotLegs, 'Legs'],
	[ItemSlot.ItemSlotFeet, 'Feet'],
	[ItemSlot.ItemSlotFinger1, 'Finger 1'],
	[ItemSlot.ItemSlotFinger2, 'Finger 2'],
	[ItemSlot.ItemSlotTrinket1, 'Trinket 1'],
	[ItemSlot.ItemSlotTrinket2, 'Trinket 2'],
	[ItemSlot.ItemSlotMainHand, 'Main Hand'],
	[ItemSlot.ItemSlotOffHand, 'Off Hand'],
	[ItemSlot.ItemSlotRanged, 'Ranged'],
]);

export const resourceNames: Map<ResourceType, string> = new Map([
	[ResourceType.ResourceTypeNone, 'None'],
	[ResourceType.ResourceTypeHealth, 'Health'],
	[ResourceType.ResourceTypeMana, 'Mana'],
	[ResourceType.ResourceTypeEnergy, 'Energy'],
	[ResourceType.ResourceTypeRage, 'Rage'],
	[ResourceType.ResourceTypeComboPoints, 'Combo Points'],
	[ResourceType.ResourceTypeFocus, 'Focus'],
	[ResourceType.ResourceTypeRunicPower, 'Runic Power'],
	[ResourceType.ResourceTypeBloodRune, 'Blood Rune'],
	[ResourceType.ResourceTypeFrostRune, 'Frost Rune'],
	[ResourceType.ResourceTypeUnholyRune, 'Unholy Rune'],
	[ResourceType.ResourceTypeDeathRune, 'Death Rune'],
]);

export const resourceColors: Map<ResourceType, string> = new Map([
	[ResourceType.ResourceTypeNone, '#ffffff'],
	[ResourceType.ResourceTypeHealth, '#22ba00'],
	[ResourceType.ResourceTypeMana, '#2e93fa'],
	[ResourceType.ResourceTypeEnergy, '#ffd700'],
	[ResourceType.ResourceTypeRage, '#ff0000'],
	[ResourceType.ResourceTypeComboPoints, '#ffa07a'],
	[ResourceType.ResourceTypeFocus, '#cd853f'],
	[ResourceType.ResourceTypeRunicPower, '#5b99ee'],
	[ResourceType.ResourceTypeBloodRune, '#ff0000'],
	[ResourceType.ResourceTypeFrostRune, '#0000ff'],
	[ResourceType.ResourceTypeUnholyRune, '#00ff00'],
	[ResourceType.ResourceTypeDeathRune, '#8b008b'],
]);

export function stringToResourceType(str: string): ResourceType {
	for (const [key, val] of resourceNames) {
		if (val.toLowerCase() == str.toLowerCase()) {
			return key;
		}
	}
	return ResourceType.ResourceTypeNone;
}

export const sourceNames: Map<SourceFilterOption, string> = new Map([
	[SourceFilterOption.SourceUnknown, 'Unknown'],
	[SourceFilterOption.SourceCrafting, 'Crafting'],
	[SourceFilterOption.SourceQuest, 'Quest'],
	[SourceFilterOption.SourceDungeon, 'Dungeon'],
	[SourceFilterOption.SourceDungeonH, 'Dungeon (H)'],
	[SourceFilterOption.SourceDungeonTRA, 'Dungeon (TRA)'],
	[SourceFilterOption.SourceDungeonTRB, 'Dungeon (TRB)'],
	[SourceFilterOption.SourceRaid10, 'Raid (10N)'],
	[SourceFilterOption.SourceRaid10H, 'Raid (10H)'],
	[SourceFilterOption.SourceRaid25, 'Raid (25N)'],
	[SourceFilterOption.SourceRaid25H, 'Raid (25H)'],
]);
export const raidNames: Map<RaidFilterOption, string> = new Map([
	[RaidFilterOption.RaidUnknown, 'Unknown'],
	[RaidFilterOption.RaidVanilla, 'Vanilla'],
	[RaidFilterOption.RaidTbc, 'TBC'],
	[RaidFilterOption.RaidNaxxramas, 'Naxxramas'],
	[RaidFilterOption.RaidEyeOfEternity, 'Eye of Eternity'],
	[RaidFilterOption.RaidObsidianSanctum, 'Obsidian Sanctum'],
	[RaidFilterOption.RaidVaultOfArchavon, 'Vault of Archavon'],
	[RaidFilterOption.RaidUlduar, 'Ulduar'],
	[RaidFilterOption.RaidTrialOfTheCrusader, 'Trial of the Crusader'],
	[RaidFilterOption.RaidOnyxiasLair, "Onyxia's Lair"],
	[RaidFilterOption.RaidIcecrownCitadel, 'Icecrown Citadel'],
	[RaidFilterOption.RaidRubySanctum, 'Ruby Sanctum'],
]);

export const difficultyNames: Map<DungeonDifficulty, string> = new Map([
	[DungeonDifficulty.DifficultyUnknown, 'Unknown'],
	[DungeonDifficulty.DifficultyNormal, 'N'],
	[DungeonDifficulty.DifficultyHeroic, 'H'],
	[DungeonDifficulty.DifficultyTitanRuneAlpha, 'TRA'],
	[DungeonDifficulty.DifficultyTitanRuneBeta, 'TRB'],
	[DungeonDifficulty.DifficultyRaid10, '10N'],
	[DungeonDifficulty.DifficultyRaid10H, '10H'],
	[DungeonDifficulty.DifficultyRaid25, '25N'],
	[DungeonDifficulty.DifficultyRaid25H, '25H'],
]);

export const REP_LEVEL_NAMES: Record<RepLevel, string> = {
	[RepLevel.RepLevelUnknown]: 'Unknown',
	[RepLevel.RepLevelHated]: 'Hated',
	[RepLevel.RepLevelHostile]: 'Hostile',
	[RepLevel.RepLevelUnfriendly]: 'Unfriendly',
	[RepLevel.RepLevelNeutral]: 'Neutral',
	[RepLevel.RepLevelFriendly]: 'Friendly',
	[RepLevel.RepLevelHonored]: 'Honored',
	[RepLevel.RepLevelRevered]: 'Revered',
	[RepLevel.RepLevelExalted]: 'Exalted',
};

export const REP_FACTION_NAMES: Record<RepFaction, string> = {
	[RepFaction.RepFactionUnknown]: 'Unknown',
};
